import React from "react";
import { Form } from "antd";
import DOMPurify from "dompurify";
import "./QsetViewJD.scss";

/**
 * QsetViewJD
 * Read-only "detail card" view for a single JD, used in place of a table row.
 *
 * Props:
 *  - jd:              a single JD object, same shape as an entry in myJdArray
 *  - qsetUserDetails: the object read from localStorage("qsetUserDetails"), used for Payroll
 *  - appliedOn:       string date the application was made on (e.g. sOwner.sourceDate)
 *  - applicationMode: e.g. "Added By HR" / "Self"
 */
export default function QSETViewJD({
  jd,
  qsetUserDetails,
  appliedOn,
  applicationMode = "Added By HR",
}) {
  const formatArrayToString = (array) => {
    return array
      ?.map((item) => item?.charAt(0)?.toUpperCase() + item?.slice(1))
      .join(", ");
  };

  const assignment = jd?.JDAssignment?.Assignments?.[0];
  const hideCtc = jd?.jdnujobs?.hidectc;

  return (
    <div className="qsetViewJD-form-wrapper">
      <div className="jd-view-value-title">{jd?.JDDetails?.Title || "-"}</div>

      <Form className="jd-view-form" layout="vertical" disabled={true}>
        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Applied On">
            <div className="jd-view-value">{appliedOn || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Application Mode">
            <div className="jd-view-value">{applicationMode || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Payroll">
            <div className="jd-view-value">
              {qsetUserDetails?.companyname?.replaceAll("+", " ") || "-"}
            </div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Job ID">
            <div className="jd-view-value">{jd?.QhCompanyJID || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Job Designation">
            <div className="jd-view-value">
              {jd?.JDDetails?.Designation || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Job Category">
            <div className="jd-view-value">
              {jd?.JDDetails?.JobCategory?.toUpperCase() || "-"}
            </div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Job Type">
            <div className="jd-view-value">{jd?.JDDetails?.JobType || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Job Time">
            <div className="jd-view-value">{jd?.JDDetails?.JobTime || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Company Name">
            <div className="jd-view-value">{jd?.CompanyName || "-"}</div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Client Name">
            <div className="jd-view-value">
              {jd?.JDClient?.ClientName || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="End Client">
            <div className="jd-view-value">
              {jd?.JDClient?.EndClient || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Role">
            <div className="jd-view-value">{jd?.JDRequired?.Role || "-"}</div>
          </Form.Item>
        </div>

        <Form.Item className="jd-view-field-item" label="Role Type">
          <div className="jd-view-value">{jd?.JDRequired?.RoleType || "-"}</div>
        </Form.Item>

        <Form.Item className="jd-view-field-item" label="Role Details">
          <div
            className="jd-view-field-item"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(jd?.JDRequired?.RoleDetails || "-"),
            }}
          />
        </Form.Item>

        <Form.Item className="jd-view-field-item" label="Required Skills">
          <div
            className="jd-view-field-item"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(jd?.JDRequired?.Skills || "-"),
            }}
          />
        </Form.Item>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Experience (Yrs)">
            <div className="jd-view-value">
              {(jd?.JDRequired?.MinExperience ?? "-") +
                " to " +
                (jd?.JDRequired?.MaxExperience ?? "-")}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Offered CTC (LPA)">
            <div className="jd-view-value">
              {hideCtc
                ? "Not Disclosed"
                : (jd?.JDProject?.MinCtc ?? "-") +
                  " to " +
                  (jd?.JDProject?.MaxCtc ?? "-")}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Notice Period">
            <div className="jd-view-value">
              {jd?.JDRequired?.NoticePeriod < 15
                ? "Immediate Joiners"
                : jd?.JDRequired?.NoticePeriod
                  ? jd.JDRequired.NoticePeriod + " Days"
                  : "-"}
            </div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Education">
            <div className="jd-view-value">
              {formatArrayToString(jd?.JDRequired?.Education?.[0]) || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Work Location">
            <div className="jd-view-value">
              {jd?.JDRequired?.WorkLocation?.join(", ") || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Work Mode">
            <div className="jd-view-value">
              {jd?.JDRequired?.WorkMode || "-"}
            </div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="HR Name">
            <div className="jd-view-value">
              {assignment?.AssignedToName || jd?.JDAssignment?.Spoc || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="HR Email">
            <div className="jd-view-value">
              {assignment?.AssignedToEmail || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="HR Phone">
            <div className="jd-view-value">
              {assignment?.AssignedToPhone || "-"}
            </div>
          </Form.Item>
        </div>

        <div className="triple-input-wrapper">
          <Form.Item className="jd-view-field-item" label="Posted By">
            <div className="jd-view-value">{jd?.JDOther?.PostedBy || "-"}</div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Posting Date">
            <div className="jd-view-value">
              {jd?.JDOther?.PostingDate || "-"}
            </div>
          </Form.Item>
          <Form.Item className="jd-view-field-item" label="Assigned On">
            <div className="jd-view-value">
              {jd?.JDAssignment?.AssignedOn || "-"}
            </div>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
}
